import _ from 'lodash'
import { useContext, useEffect, useMemo, useState } from 'react'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'
import { useDebouncedCallback } from 'use-debounce'

import { useRequestHandler } from '@/hooks/useRequestHandler'

import { AddDoctorFormValues } from '@/components/group/AddDoctorFormDialog'
import AddDoctorDialog from '@/containers/group/AddDoctorDialog'

import { useAppDispatch, useAppSelector } from '@/redux/rootReducer'
import { addMedecin, selectMedecinCreateRequestTracker } from '@/redux/reducers/medecin.reducer'
import { getStaffs, selectStaffListRequestTracker, selectStaffs, selectStaffSearchParams, selectStaffSearchResult } from '@/redux/reducers/staff.reducer'

import { sanitizeStaffCreation } from '@/utils/staff'

import SelectDoctorStep, { rowsPerPageOptions } from '../components/SelectDoctorStep'
import { ExpertiseValidationStepperContext } from '../ExpertiseValidationPage'

export function SelectDoctorStepContainer() {
  const dispatch = useAppDispatch()
  const { handleChange: handleStepperValuesChange } = useContext(ExpertiseValidationStepperContext)
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()

  const [addDoctorDialogOpen, setAddDoctorDialogOpen] = useState<boolean>(false)

  const doctors = useAppSelector(selectStaffs)
  const doctorListRequestTracker = useAppSelector(selectStaffListRequestTracker)
  const doctorSearchParams = useAppSelector(selectStaffSearchParams)
  const doctorSearchResult = useAppSelector(selectStaffSearchResult)

  const data = useMemo(() => {
    return _.map(doctors, (staff) => ({
      id: staff._id,
      name: staff.userInfo.name,
      surname: staff.userInfo.surname,
      gender: staff.userInfo.gender || 'I',
      city: staff.contactInfo?.city,
      cps: staff.cps,
      email: staff.email,
    }))
  }, [doctors])

  const addDoctorRequestHandler = useRequestHandler({
    onSuccess: () => {
      enqueueSnackbar(t('notification.medecin.create.success'), { variant: 'success' })
      setAddDoctorDialogOpen(false)
    },
    onError: () => enqueueSnackbar(t('notification.medecin.create.error'), { variant: 'error' }),
    requestTrackerSelector: selectMedecinCreateRequestTracker,
  })

  const handleSearchDoctor = useDebouncedCallback((text: string) => {
    const query = {
      name: text,
      surname: text,
    }
    dispatch(getStaffs({ ...query, status: 'MEDECIN' }, { limit: _.get(doctorSearchParams, 'options.limit', rowsPerPageOptions[0]) }))
  }, 300)

  useEffect(() => {
    dispatch(getStaffs({ surname: '', status: 'MEDECIN' }))
  }, [dispatch])

  const handleAddDoctor = (values: AddDoctorFormValues) => {
    addDoctorRequestHandler.dispatch(addMedecin(sanitizeStaffCreation(values)))
  }

  const handleChangePage = (newPage: number, rowsPerPage: number) => {
    newPage = newPage - 1
    dispatch(getStaffs({ name: '', surname: '', status: 'MEDECIN' }, { skip: newPage * rowsPerPage }))
  }

  const handleClickAddDoctor = () => {
    setAddDoctorDialogOpen(true)
  }

  const handleSelectDoctor = (doctorId: string) => {
    handleStepperValuesChange('medecin', doctorId)
  }

  const toggleAddDoctorDialog = () => {
    setAddDoctorDialogOpen((prev) => !prev)
  }

  return (
    <>
      <SelectDoctorStep
        data={data}
        isSearchingDoctors={doctorListRequestTracker.loading}
        onAddDoctor={handleClickAddDoctor}
        onPageChange={handleChangePage}
        onSelectDoctor={handleSelectDoctor}
        onSearchDoctor={handleSearchDoctor}
        totalCount={_.get(doctorSearchResult, 'totalCount', 0)}
      />
      <AddDoctorDialog
        isAdding={addDoctorRequestHandler.requestTracker.loading}
        open={addDoctorDialogOpen}
        onAddDoctor={handleAddDoctor}
        onClose={toggleAddDoctorDialog}
      />
    </>
  )
}

export default SelectDoctorStepContainer
